import React from 'react';
import { useClearCache } from '@seccl/api-swr';
import { Permission, Resource } from '@seccl/client';
import { stringifyQuery } from '@seccl/ui';
import { Header as UIHeader } from '@seccl/ui/components/header';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { CUSTODIAN_UI_FEATURE_FLAG } from '../../constants/firmFeatureFlags';
import { useFirm } from '../../context/firm.context';
import { ToolsContext } from '../../context/tools.context';
import { UserContext } from '../../context/user.context';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useScopes } from '../../hooks/useScope';
import { routes } from '../../routes';
import { useCanViewClientGroups } from '../client/clientGroupsAccess';
import { ClientSearchInput } from '../client/clientSearchInput';
import { CLIENT_NODE_SWITCH_FEATURE_FLAG } from '../clientNodeSwitch/clientNodeSwitchAccess';
import { FIRM_NODES_UI_FEATURE_FLAG, hasFirmNodesViewAccess } from '../firmNodes/firmNodesAccess';
import { defaultTransfersColumnState } from '../tables/allTransfersTable.constants';
import { defaultTransactionsColumnState } from '../tables/transactions/allTransactionsTable.constants';

const FeatureFlagWizard = dynamic(() => import('../flags/featureFlagWizard').then(m => m.FeatureFlagWizard), {
  ssr: false,
});

const FirmNodeWizard = dynamic(() => import('../firmNodeWizard/firmNodeWizard').then(m => m.FirmNodeWizard), {
  ssr: false,
});

/**
 * Seccl Pro header that appears on all authorised pages.
 */
export const Header: React.FC = () => {
  const { hasAccess, hasScope } = useScopes();
  const { hasFeatureFlag } = useFeatureFlag();
  const clearCache = useClearCache();
  const router = useRouter();
  const { showDevTools } = React.useContext(ToolsContext);
  const { canViewMigrations } = React.useContext(UserContext);

  const canViewAdvisoryModels = hasAccess(Resource.AdvisoryModel);
  const canViewDiscretionaryModels = hasAccess(Resource.Model);
  const canViewUsers = hasAccess(Resource.User);
  const canViewInstruments = hasAccess(Resource.Instrument);
  const { isCustodianFirm } = useFirm();
  const hideInstruments = hasFeatureFlag(CUSTODIAN_UI_FEATURE_FLAG) && isCustodianFirm;
  const canViewTransactions = hasAccess(Resource.Transaction);
  const canManageFirm = hasScope(Resource.FirmManagement, Permission.ReadWrite);
  const canViewIllustrations =
    !hasFeatureFlag('seccl-34452-taxwrappers-enhancedclient-readwrite-scope') ||
    hasScope(Resource.ClientEnhanced, Permission.ReadWrite);
  const canClientNodeSwitch =
    hasScope(Resource.NodeSwitch, Permission.ReadWrite) && hasFeatureFlag(CLIENT_NODE_SWITCH_FEATURE_FLAG);
  const canViewFirmNodes = hasFirmNodesViewAccess(hasScope) && hasFeatureFlag(FIRM_NODES_UI_FEATURE_FLAG);
  const isEnhancedTransactionsTableEnabled = hasFeatureFlag('seccl-31363-transactions-enhanced-ui');
  const isNewClientsMenuEnabled = hasFeatureFlag('seccl-33899-platform-ui-new-clients-menu');
  const canViewClientGroups = useCanViewClientGroups();
  const hideIllustrationsAndReporting = hasFeatureFlag(CUSTODIAN_UI_FEATURE_FLAG);

  return (
    <>
      <UIHeader
        linkComponent={Link}
        navigationItems={[
          {
            content: 'Dashboard',
            href: routes.dashboard,
          },
          isNewClientsMenuEnabled
            ? {
                content: 'Clients',
                menuItems: [
                  { content: 'Clients', href: routes.clients.root, unmaskForAnalytics: true },
                  { content: 'Accounts', href: routes.accounts.root, unmaskForAnalytics: true },
                  {
                    content: 'Client groups',
                    href: routes.clients.groups().root,
                    unmaskForAnalytics: true,
                    hidden: !canViewClientGroups,
                  },
                ],
              }
            : {
                content: 'Clients',
                href: routes.clients.root,
              },
          {
            content: 'Tools',
            menuItems: [
              {
                content: 'Models',
                href: canViewAdvisoryModels ? routes.models('advisory').root : routes.models('discretionary').root,
                hidden: !canViewAdvisoryModels && !canViewDiscretionaryModels,
                unmaskForAnalytics: true,
              },
              {
                content: 'Illustrations',
                href: routes.illustrations.root,
                hidden: hideIllustrationsAndReporting || !canViewIllustrations,
                unmaskForAnalytics: true,
              },
              {
                content: 'Transactions',
                href: `${routes.transactions.root}${
                  isEnhancedTransactionsTableEnabled ? `?${stringifyQuery(defaultTransactionsColumnState)}` : ''
                }`,
                hidden: !canViewTransactions,
                unmaskForAnalytics: true,
              },
            ],
          },
          {
            content: 'Instruments',
            href: routes.instruments.root,
            hidden: hideInstruments || !canViewInstruments,
          },
          {
            content: 'Reporting',
            href: routes.reporting,
            hidden: hideIllustrationsAndReporting,
          },
          {
            content: 'Transfers',
            href: routes.transfers(stringifyQuery(defaultTransfersColumnState)),
          },
          {
            content: 'Migrations',
            href: routes.migrations.root,
            hidden: !canViewMigrations,
          },
        ]}
        searchComponent={
          <ClientSearchInput
            onSelectClient={clientId => {
              if (clientId) {
                router.push(routes.clients.client(clientId).accounts);
              }
            }}
          />
        }
        settingsMenu={{
          menuItems: [
            {
              content: 'Firm users',
              href: routes.users.root,
              hidden: !canViewUsers,
            },
            {
              content: 'Firm customisation',
              href: routes.firmSettings.professional.theme,
              hidden: !canManageFirm,
            },
            {
              content: 'Client node switch',
              href: routes.nodeSwitch,
              hidden: !canClientNodeSwitch,
            },
            {
              content: 'Firm nodes',
              href: routes.firmNodes.root,
              hidden: !canViewFirmNodes,
            },
            {
              separator: true,
              content: 'Log out',
              onClick: clearCache,
              href: routes.auth.auth0.logout,
            },
          ],
        }}
      >
        {showDevTools && (
          <>
            <FirmNodeWizard />
            <FeatureFlagWizard />
          </>
        )}
      </UIHeader>
    </>
  );
};
